import { type Change, diffLines, diffWords } from 'diff';

import {
	CLARITY_MARKDOWN_DIFF_GRANULARITY,
	type ClarityMarkdownDiffGranularity,
} from './constants/clarity-markdown-diff-granularity.constant';
import type { ClarityMarkdownDiffResult } from './models/clarity-markdown-diff-result.model';
import type {
	ClarityMarkdownDiffChange,
	ClarityMarkdownDiffRange,
	ClarityMarkdownDiffSource,
} from './models/clarity-markdown-diff-source.model';
import { stripFrontmatter } from './strip-frontmatter.util';

/** Brain's `DiffMarkerType`. */
type DiffMarkerType = ClarityMarkdownDiffChange | 'unchanged';

/** Brain's `DiffBlock`: one change value and what it did. */
type DiffBlock = {
	readonly content: string;
	readonly type: DiffMarkerType;
};

type DiffStats = ClarityMarkdownDiffResult['stats'];

/** The blocks of a computed diff and how finely they mark its changes. */
type BodyDiff = {
	readonly blocks: readonly DiffBlock[];
	readonly granularity: ClarityMarkdownDiffGranularity;
};

/** A replaced paragraph re-diffed word by word: its blocks, what that spent, and whether it stayed line-level. */
type RefinedReplacement = {
	readonly blocks: readonly DiffBlock[];
	readonly work: number;
	readonly isLineLevel: boolean;
};

const WHITESPACE = /\s+/;

const LINE_BREAK = '\n';
const BLANK_LINE = '\n\n';

/** The most characters, both bodies together, that are diffed and rendered; a longer pair gets only its counts. */
const MAX_DIFFED_LENGTH = 1_000_000;

/** The largest product of the two bodies' lengths diffed word by word with no edit budget. */
const UNBOUNDED_WORD_DIFF_LENGTH_PRODUCT = 250_000_000;

/** The most tokens the word pass, or lines the line pass, may insert and delete before jsdiff gives up. */
const EDIT_LENGTH_LIMIT = 1_000;

/** The most tokens re-diffing one changed paragraph word by word may insert and delete. */
const PARAGRAPH_EDIT_LENGTH_LIMIT = 400;

/** The tokens every changed paragraph may insert and delete when re-diffed, however much the others spent. */
const PARAGRAPH_EDIT_LENGTH_FLOOR = 50;

/** What re-diffing the changed paragraphs word by word may spend in all, as the sum of each edit length squared. */
const REFINEMENT_WORK_LIMIT = EDIT_LENGTH_LIMIT ** 2;

function toMarkerType(change: Change): DiffMarkerType {
	if (change.added) {
		return 'added';
	}

	return change.removed ? 'removed' : 'unchanged';
}

/** Brain's `changesToBlocks`. */
function changesToBlocks(changes: readonly Change[]): DiffBlock[] {
	return changes.map(change => ({ content: change.value, type: toMarkerType(change) }));
}

/** One value of the line pass as a block; a value holding only whitespace is unchanged. */
function toLineBlock(change: Change): DiffBlock {
	return { content: change.value, type: change.value.trim() === '' ? 'unchanged' : toMarkerType(change) };
}

/** The unchanged line breaks that make `text` end with `ending`, as a block; none when it already does. */
function toMissingBreak(text: string, ending: typeof LINE_BREAK | typeof BLANK_LINE): DiffBlock[] {
	const present = text.endsWith(BLANK_LINE) ? BLANK_LINE.length : Number(text.endsWith(LINE_BREAK));
	const missing = ending.slice(present);

	return missing === '' ? [] : [{ content: missing, type: 'unchanged' }];
}

/** The whole `original` removed and the whole `updated` added, `updated` opening a block of its own. */
function toWholeDiff(original: string, updated: string): BodyDiff {
	return {
		blocks: [
			{ content: original, type: 'removed' },
			...toMissingBreak(original, BLANK_LINE),
			{ content: updated, type: 'added' },
		],
		granularity: CLARITY_MARKDOWN_DIFF_GRANULARITY.WHOLE,
	};
}

/** How many tokens `changes` insert and delete. */
function editLengthOf(changes: readonly Change[]): number {
	return changes.reduce((total, change) => (change.added || change.removed ? total + change.count : total), 0);
}

/**
 * A removed line value and the added one after it, re-diffed word by word within `workLeft`.
 *
 * @param removed The line pass's removed value.
 * @param added The line pass's added value that follows it.
 * @param workLeft What the refinement may still spend, as a squared edit length.
 * @returns The blocks, what they spent, and whether they stayed line-level, `added` then opening a line of its own.
 */
function refineReplacement(removed: Change, added: Change, workLeft: number): RefinedReplacement {
	const editLengthLimit = Math.max(
		PARAGRAPH_EDIT_LENGTH_FLOOR,
		Math.min(PARAGRAPH_EDIT_LENGTH_LIMIT, Math.floor(Math.sqrt(Math.max(workLeft, 0)))),
	);
	const words = diffWords(removed.value, added.value, { maxEditLength: editLengthLimit });

	if (words === undefined) {
		return {
			blocks: [toLineBlock(removed), ...toMissingBreak(removed.value, LINE_BREAK), toLineBlock(added)],
			work: editLengthLimit ** 2,
			isLineLevel: true,
		};
	}

	return { blocks: changesToBlocks(words), work: editLengthOf(words) ** 2, isLineLevel: false };
}

/** The blocks of the line pass's `lines`, `mixed` when a replaced paragraph stayed line-level. */
function refineLineChanges(lines: readonly Change[]): BodyDiff {
	const blocks: DiffBlock[] = [];
	let workLeft = REFINEMENT_WORK_LIMIT;
	let hasLineLevelChange = false;
	let index = 0;

	while (index < lines.length) {
		const change = lines[index];
		const next = lines.at(index + 1);

		if (change.removed && next?.added) {
			const refined = refineReplacement(change, next, workLeft);

			blocks.push(...refined.blocks);
			workLeft -= refined.work;
			hasLineLevelChange ||= refined.isLineLevel;
			index += 2;
		} else {
			blocks.push(toLineBlock(change));
			index += 1;
		}
	}

	return {
		blocks,
		granularity: hasLineLevelChange
			? CLARITY_MARKDOWN_DIFF_GRANULARITY.MIXED
			: CLARITY_MARKDOWN_DIFF_GRANULARITY.WORD,
	};
}

/** The blocks of two non-empty bodies and their granularity. */
function computeBodyDiff(original: string, updated: string): BodyDiff {
	if (original === updated) {
		return {
			blocks: [{ content: updated, type: 'unchanged' }],
			granularity: CLARITY_MARKDOWN_DIFF_GRANULARITY.WORD,
		};
	}

	if (original.length + updated.length > MAX_DIFFED_LENGTH) {
		return {
			blocks: [
				{ content: original, type: 'removed' },
				{ content: updated, type: 'added' },
			],
			granularity: CLARITY_MARKDOWN_DIFF_GRANULARITY.UNRENDERED,
		};
	}

	if (original.length * updated.length <= UNBOUNDED_WORD_DIFF_LENGTH_PRODUCT) {
		return {
			blocks: changesToBlocks(diffWords(original, updated)),
			granularity: CLARITY_MARKDOWN_DIFF_GRANULARITY.WORD,
		};
	}

	const words = diffWords(original, updated, { maxEditLength: EDIT_LENGTH_LIMIT });

	if (words !== undefined) {
		return { blocks: changesToBlocks(words), granularity: CLARITY_MARKDOWN_DIFF_GRANULARITY.WORD };
	}

	const lines = diffLines(original, updated, { maxEditLength: EDIT_LENGTH_LIMIT });

	return lines === undefined ? toWholeDiff(original, updated) : refineLineChanges(lines);
}

/** Brain's `countWords`: whitespace-separated chunks, so a lone `*` or `.` is a word. */
function countWords(text: string): number {
	return text.split(WHITESPACE).filter(word => word.length > 0).length;
}

/** Brain's statistics loop over the blocks of a computed diff. */
function countStats(blocks: readonly DiffBlock[]): DiffStats {
	let addedWords = 0;
	let removedWords = 0;
	let addedBlocks = 0;
	let removedBlocks = 0;
	let unchangedBlocks = 0;

	for (const block of blocks) {
		const wordCount = countWords(block.content);
		if (block.type === 'added') {
			addedWords += wordCount;
			addedBlocks++;
		} else if (block.type === 'removed') {
			removedWords += wordCount;
			removedBlocks++;
		} else {
			unchangedBlocks++;
		}
	}

	return { addedBlocks, removedBlocks, unchangedBlocks, addedWords, removedWords };
}

/**
 * The merged source and its ranges — what replaces brain's `blocksToMarkedMarkdown`: every value concatenated in diff
 * order, unescaped, and one range per added or removed value.
 */
function toDiffSource(blocks: readonly DiffBlock[]): ClarityMarkdownDiffSource {
	const ranges: ClarityMarkdownDiffRange[] = [];
	let source = '';

	for (const block of blocks) {
		const start = source.length;
		source += block.content;
		if (block.type !== 'unchanged') {
			ranges.push({ start, end: source.length, change: block.type });
		}
	}

	return { source, ranges };
}

/**
 * The result for a list of blocks. Brain writes its three edge-case results out by hand; counting their single block
 * gives the same values, so every branch goes through here.
 */
function toResult(
	blocks: readonly DiffBlock[],
	granularity: ClarityMarkdownDiffGranularity = CLARITY_MARKDOWN_DIFF_GRANULARITY.WORD,
): ClarityMarkdownDiffResult {
	const stats = countStats(blocks);

	return {
		...toDiffSource(blocks),
		hasChanges: stats.addedBlocks > 0 || stats.removedBlocks > 0,
		stats,
		granularity,
	};
}

/** Ported from Clarity `apps/web/src/features/canvas/utils/markdownDiff.ts` (commit `6750a5f43`). */
/**
 * Brain's `computeMarkdownDiff`: jsdiff's `diffWords` over the whole body while it stays within its edit budget, else
 * `diffLines` refined paragraph by paragraph, else the two whole bodies, with brain's edge cases and statistics. It
 * answers the structured-highlight contract instead of brain's marked string (parity exception 7, SEC-4), so nothing in a
 * file's content can produce a highlight.
 *
 * It never refuses a pair: `granularity` says whether the ranges are word-level, include line-level ranges, are the
 * two whole bodies, or — past `MAX_DIFFED_LENGTH` — absent, with an empty source and counts over the two whole bodies.
 *
 * Pass the raw contents: the frontmatter is stripped here, once, as brain's `FileVersionDiff` strips both sides before its
 * call. The edge cases therefore see the bodies — a side that holds only frontmatter is an empty side.
 *
 * jsdiff is 8.0.3, the first release outside CVE-2026-24001, where brain pins 8.0.2. The two tokenise three characters
 * differently — the soft hyphen, `×` and `÷` — which is parity exception 13.
 */
export function computeClarityMarkdownDiff(
	originalContent: string | null | undefined,
	newContent: string | null | undefined,
): ClarityMarkdownDiffResult {
	const original = stripFrontmatter(originalContent ?? '');
	const updated = stripFrontmatter(newContent ?? '');

	if (!original && !updated) {
		return toResult([]);
	}

	if (!original) {
		return toResult([{ content: updated, type: 'added' }]);
	}

	if (!updated) {
		return toResult([{ content: original, type: 'removed' }]);
	}

	const body = computeBodyDiff(original, updated);
	const result = toResult(body.blocks, body.granularity);

	return body.granularity === CLARITY_MARKDOWN_DIFF_GRANULARITY.UNRENDERED
		? { ...result, source: '', ranges: [] }
		: result;
}
